
import React from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  TextField,
  InputAdornment,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AppTable from 'components/Shared/AppTable';
import BreadcrumbLayout from 'layouts/BreadcrumbLayout';
import { useTablePagination } from 'hooks';
import { useLazyListGetDocumentsExpiryQuery } from "store/services/reportApi";
import ExportVendorDocumentExpiryReport from "./ExportVendorDocumentExpiryReport";

const VendorDocumentExpiryReport = () => {
  const [expiryFilter, setExpiryFilter] = React.useState('week');
  const [searchTerm, setSearchTerm] = React.useState('');
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const [fetchDocuments, { data: reportData, isLoading, isFetching, error }] = useLazyListGetDocumentsExpiryQuery();

  React.useEffect(() => {
    const daysThreshold = expiryFilter === 'week' ? 7 : 30;
    fetchDocuments({ 
      page: currentPage,
      limit: perPage,
      daysThreshold,
      ...(searchTerm && { search: searchTerm })
    });
  }, [fetchDocuments, currentPage, perPage, expiryFilter, searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

   const columns = [
    {
      name: 'Title',
      selector: (row) => row.title,
      cell: (row) => row.title || 'N/A',
    },
    {
      name: 'Owner Name',
      selector: (row) => row.sourceRef?.ownerName,
      cell: (row) => row.sourceRef?.ownerName || 'N/A',
    },
    {
      name: 'Shop Name',
      selector: (row) => row.sourceRef?.shopName,
      cell: (row) => row.sourceRef?.shopName || 'N/A',
    },
    {
      name: 'Document Type',
      selector: (row) => row.vendorDocumentType?.name,
      cell: (row) => row.vendorDocumentType?.name || 'N/A',
    },
    {
      name: 'Expiry Date',
      selector: (row) => row.expiryDate,
      cell: (row) => new Date(row.expiryDate).toLocaleDateString() || 'N/A',
    },
    {
      name: 'Days Until Expiry',
      selector: (row) => row.daysUntilExpiry,
      cell: (row) => (
        <Chip 
          label={`${row.daysUntilExpiry} days`}
        />
      ),
    },
  ];

  const breadcrumbs = [
    { title: 'Reports', path: '/app/crm/reports/list' },
    { title: 'Vendor Document Expiry Report' },
  ];

return (
  <BreadcrumbLayout breadcrumbs={breadcrumbs}>
    <BreadcrumbLayout.Error error={error} />
    <BreadcrumbLayout.Paper>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ flex: 1, justifyContent: "flex-end" }}
          >
            <TextField
              placeholder="Search vendors..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: {
                  height: '40px', 
                  '& input': {
                    padding: '8.5px 14px',
                  }
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '40px' 
                }
              }}
            />

            <ToggleButtonGroup
              color="primary"
              value={expiryFilter}
              exclusive
              size="medium"
              onChange={(e, newFilter) => newFilter && setExpiryFilter(newFilter)}
              aria-label="Expiry Filter"
              sx={{
                '& .MuiToggleButton-root': {
                  px: 3,
                  py: 1,
                  fontWeight: 'bold',
                  height: '40px' 
                }
              }}
            >
              <ToggleButton value="week">1 Week</ToggleButton>
              <ToggleButton value="month">1 Month</ToggleButton>
            </ToggleButtonGroup>

            <ExportVendorDocumentExpiryReport />
          </Stack>
        </Box>
      </Box>

      <AppTable
        columns={columns}
        data={reportData?.data ?? []}
        progressPending={isLoading || isFetching}
        paginationServer
        paginationPerPage={perPage}
        paginationTotalRows={reportData?.totalItems ?? 0}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
      />
    </BreadcrumbLayout.Paper>
  </BreadcrumbLayout>
);
};

export default VendorDocumentExpiryReport;